import React, { useRef, useEffect, useCallback, useState } from 'react';
import { useDavyAgentCall, LoadingState } from './useDavyAgentCall';
import { MessageBubble } from './DavyComponents';
import DavySummary from './DavySummary';

interface DavyVoiceCallProps {
  onBack: () => void;
}

const DavyVoiceCall: React.FC<DavyVoiceCallProps> = ({ onBack }) => {
  const scrollAnchorRef = useRef<HTMLDivElement>(null);
  const desktopScrollAnchorRef = useRef<HTMLDivElement>(null);
  const [showEndModal, setShowEndModal] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [finalDuration, setFinalDuration] = useState('0:00');
  const wasActiveRef = useRef(false);

  const {
    loadingState,
    error,
    transcript,
    isCallActive,
    callDuration,
    startCall,
    endCall,
    formatDuration,
    isSpeaking,
  } = useDavyAgentCall();

  const isConnecting = loadingState === LoadingState.CONNECTING;

  // Detect call end → show summary
  useEffect(() => {
    if (isCallActive) {
      wasActiveRef.current = true;
    } else if (wasActiveRef.current && loadingState === LoadingState.INITIAL) {
      setFinalDuration(formatDuration(callDuration));
      setShowSummary(true);
      wasActiveRef.current = false;
    }
  }, [isCallActive, loadingState, callDuration, formatDuration]);

  const scrollToBottom = useCallback(() => {
    scrollAnchorRef.current?.scrollIntoView({ block: 'end', behavior: 'instant' as ScrollBehavior });
    desktopScrollAnchorRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [transcript, scrollToBottom]);

  const getStatusText = () => {
    if (isConnecting) return 'Connecting...';
    if (isCallActive) return 'Connected to Davy';
    return 'Ready to connect';
  };
  const getStatusBg = () => isConnecting ? '#FFF4E6' : isCallActive ? '#E7F8F3' : '#EAEDEF';
  const getStatusColor = () => isConnecting ? '#C77700' : isCallActive ? '#058A62' : '#58595A';

  const handleEndConfirm = () => {
    setFinalDuration(formatDuration(callDuration));
    endCall();
    setShowEndModal(false);
  };

  if (showSummary) {
    return (
      <DavySummary
        transcript={transcript}
        duration={finalDuration}
        onAskAgain={() => setShowSummary(false)}
      />
    );
  }

  const MicIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M10 12.5a2.5 2.5 0 0 0 2.5-2.5V5a2.5 2.5 0 0 0-5 0v5a2.5 2.5 0 0 0 2.5 2.5Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M16.25 10a6.25 6.25 0 0 1-12.5 0M10 16.25V18.75" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const BackArrow = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M10 8L6 12M6 12L10 16M6 12H18" stroke="#58595A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  return (
    <div className="relative min-h-screen" style={{ backgroundColor: '#F6F8F8' }}>

      {/* Mobile */}
      <div className="md:hidden flex flex-col fixed inset-x-0 top-0" style={{ height: '100dvh', backgroundColor: '#F6F8F8' }}>
        <div className="bg-white flex flex-col items-center pb-2 pt-6 px-4 shrink-0">
          <div className="flex items-center justify-between w-full py-2">
            <button onClick={onBack} className="w-6 h-6 flex items-center justify-center">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M10 8L6 12M6 12L10 16M6 12H18" stroke="#58595A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <h1 className="text-base font-semibold leading-6" style={{ color: '#161618', letterSpacing: '-0.16px' }}>Ask Davy</h1>
            {isCallActive || isConnecting ? (
              <button onClick={() => setShowEndModal(true)} disabled={isConnecting}
                className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full shrink-0 disabled:opacity-50"
                style={{ backgroundColor: '#E60D00' }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.6 3.44 2 2 0 0 1 3.57 1.27h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.8a16 16 0 0 0 6.09 6.09l.9-.9a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="1" y1="1" x2="23" y2="23" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </button>
            ) : (
              <div className="w-10 h-10" />
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4" style={{ minHeight: 0 }}>
          <div className="flex flex-col gap-3">
            {!isCallActive && !isConnecting && (
              <div className="flex flex-col items-center justify-center h-64 gap-4">
                {error && <p className="text-sm text-center px-6" style={{ color: '#E60D00' }}>{error}</p>}
                <button onClick={startCall} className="h-10 px-6 rounded-full flex items-center gap-2" style={{ backgroundColor: '#CC0044' }}>
                  <MicIcon />
                  <span className="text-sm text-white" style={{ fontWeight: 450 }}>Start call</span>
                </button>
              </div>
            )}
            {transcript.map((entry, i) => <MessageBubble key={i} entry={entry} />)}
            <div ref={scrollAnchorRef} className="h-px" />
          </div>
        </div>

        <div className="bg-white rounded-t-3xl p-4 flex flex-col gap-2 shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center justify-center rounded-full px-2 py-0.5" style={{ backgroundColor: getStatusBg() }}>
              <p className="text-xs leading-4" style={{ color: getStatusColor(), fontWeight: 450 }}>{getStatusText()}</p>
            </div>
            <p className="text-lg font-semibold leading-6" style={{ color: '#161618', letterSpacing: '-0.18px' }}>{formatDuration(callDuration)}</p>
          </div>
          <p className="text-[10px] leading-[14px] text-center" style={{ color: '#7E7F81', fontWeight: 450 }}>
            Davy provides general information only. Please verify important details.
          </p>
        </div>
      </div>

      {/* Desktop */}
      <div className="hidden md:flex flex-col min-h-screen">
        <div className="w-full p-2">
          <div className="w-full max-w-[1200px] mx-auto p-2 flex justify-between items-center">
            <button onClick={onBack} className="flex items-center gap-1.5 text-sm" style={{ color: '#58595A' }}>
              <BackArrow />
              Back
            </button>
            <span className="text-xl font-bold leading-7" style={{ color: '#161618', letterSpacing: '-0.3px' }}>Davy</span>
            <div className="w-16" />
          </div>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center py-10 px-4">
          <div className="flex justify-center w-full" style={{ maxWidth: '1000px', height: '576px', boxShadow: '0px 9px 20px rgba(209,209,209,0.10), 0px 37px 37px rgba(209,209,209,0.09), 0px 82px 49px rgba(209,209,209,0.05), 0px 147px 59px rgba(209,209,209,0.01)', borderRadius: '24px' }}>

            {/* Left panel */}
            <div className="flex w-[360px] shrink-0 flex-col items-center justify-center gap-6 rounded-l-3xl bg-white px-16 py-12">
              <div className="relative h-36 w-36 shrink-0">
                {isSpeaking && (
                  <>
                    <style>{`
                      @keyframes sonar { 0% { transform: scale(1); opacity: 0.4; } 100% { transform: scale(1.4); opacity: 0; } }
                      .sr { animation: sonar 2s ease-out infinite; }
                      .sr2 { animation: sonar 2s ease-out infinite 1s; }
                    `}</style>
                    <div className="absolute inset-0 rounded-full sr" style={{ backgroundColor: '#770032' }} />
                    <div className="absolute inset-0 rounded-full sr2" style={{ backgroundColor: '#770032' }} />
                  </>
                )}
                <div className="absolute left-3 top-3 w-[120px] h-[120px] rounded-full overflow-hidden" style={{ background: '#770032' }}>
                  <img src="/davy-5665cdbf.png" alt="Davy" className="w-full h-full object-cover" />
                </div>
              </div>

              <div className="flex flex-col items-center gap-2">
                <div className="flex items-center justify-center rounded-full px-2 py-0.5" style={{ backgroundColor: getStatusBg() }}>
                  <p className="text-xs leading-4" style={{ color: getStatusColor(), fontWeight: 450 }}>{getStatusText()}</p>
                </div>
                <p className="text-xl font-semibold leading-7 text-center" style={{ color: '#161618', letterSpacing: '-0.3px' }}>{formatDuration(callDuration)}</p>
              </div>

              {!isCallActive && !isConnecting ? (
                <button onClick={startCall} className="h-10 px-6 rounded-full flex items-center gap-2 cursor-pointer" style={{ backgroundColor: '#CC0044' }}>
                  <MicIcon />
                  <span className="text-sm text-white" style={{ fontWeight: 450 }}>Start call</span>
                </button>
              ) : isConnecting ? (
                <button disabled className="h-10 px-6 rounded-full flex items-center gap-2 cursor-not-allowed" style={{ backgroundColor: '#C77700' }}>
                  <span className="text-sm text-white" style={{ fontWeight: 450 }}>Connecting...</span>
                </button>
              ) : (
                <button onClick={() => setShowEndModal(true)} className="h-10 px-6 rounded-full flex items-center gap-2 cursor-pointer" style={{ backgroundColor: '#E60D00' }}>
                  <span className="text-sm text-white" style={{ fontWeight: 450 }}>End call</span>
                </button>
              )}

              {error && <p className="text-xs text-center" style={{ color: '#E60D00' }}>{error}</p>}
            </div>

            {/* Right panel */}
            <div className="relative flex-1 h-full flex flex-col rounded-r-3xl" style={{ backgroundColor: '#F6F8F8' }}>
              <div className="flex-1 overflow-y-auto py-6 pl-6 pr-8">
                <div className="flex flex-col gap-4">
                  {transcript.length === 0 && isCallActive && (
                    <div className="flex items-center justify-center h-32">
                      <p className="text-sm" style={{ color: '#ADB1B4', fontWeight: 450 }}>Speak to start the conversation…</p>
                    </div>
                  )}
                  {transcript.map((entry, i) => <MessageBubble key={i} entry={entry} />)}
                  <div ref={desktopScrollAnchorRef} className="h-px" />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 text-center">
            <p className="text-sm leading-5" style={{ color: '#58595A', fontWeight: 450, letterSpacing: '-0.084px' }}>
              Davy provides general information only. Please verify important details.
            </p>
          </div>
          <div className="mt-4 flex items-center justify-center gap-1.5">
            <span className="text-xs leading-4" style={{ color: '#7E7F81', fontWeight: 450 }}>Powered by</span>
            <img src="/logos/Hupo_Logotype_Orange(noR).svg" alt="Hupo" className="h-3.5" />
          </div>
        </div>
      </div>

      {/* End call modal */}
      {showEndModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={() => setShowEndModal(false)}>
          <div className="flex w-full max-w-[400px] flex-col gap-2 rounded-2xl bg-white px-4 py-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="8.333" stroke="#E60D00" strokeWidth="1.25" />
                <path d="M10 6.667v3.333" stroke="#E60D00" strokeWidth="1.25" strokeLinecap="round" />
                <circle cx="10" cy="13.333" r="0.417" fill="#E60D00" stroke="#E60D00" strokeWidth="0.833" />
              </svg>
              <p className="text-base font-semibold leading-6" style={{ color: '#161618', letterSpacing: '-0.16px' }}>End call with Davy?</p>
            </div>
            <p className="text-base leading-6" style={{ color: '#58595A', fontWeight: 450, letterSpacing: '-0.16px' }}>
              Davy won't be able to continue this conversation, but you can always start a new one.
            </p>
            <div className="flex items-end justify-end gap-2 pt-4">
              <button onClick={() => setShowEndModal(false)} className="flex h-10 items-center justify-center rounded-full px-6" style={{ backgroundColor: '#F9EFF2' }}>
                <p className="text-sm leading-5" style={{ color: '#CC0044', fontWeight: 450, letterSpacing: '-0.084px' }}>Continue call</p>
              </button>
              <button onClick={handleEndConfirm} className="flex h-10 items-center justify-center rounded-full px-6" style={{ backgroundColor: '#CC0044' }}>
                <p className="text-sm leading-5 text-white" style={{ fontWeight: 450, letterSpacing: '-0.084px' }}>End anyway</p>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DavyVoiceCall;
